import React, { useState } from 'react';
import { TestCase } from '../../types';
import { MessageSetter } from '../../utils/messages';
import { Icons } from '../../utils/icons';
import { theme } from '../../theme';

interface TestCaseCardProps {
  testCase: TestCase;
  isDragging: boolean;
  onDelete: (testCaseId: string) => void;
  onDragStart: (e: React.DragEvent, testCaseId: string) => void;
  onDragEnd: (e: React.DragEvent) => void;
  onEdit?: (testCaseId: string) => void;
  onView?: (testCase: TestCase) => void;
  setMessage?: MessageSetter;
  onShowChangeHistory?: (testCase: TestCase) => void;
  getUserName?: (userId?: string) => string;
  projectCode?: string; // Project code prefix for TC ID display (e.g., "FS" -> "FS-TC-42")
}

const TestCaseCard: React.FC<TestCaseCardProps> = ({
  testCase,
  isDragging,
  onDelete,
  onDragStart,
  onDragEnd,
  onEdit,
  onView,
  setMessage,
  onShowChangeHistory,
  getUserName,
  projectCode
}) => {
  // Generate display ID (e.g., "FS-TC-42" or "TC-42" if no project code)
  const displayId = testCase.testCaseNumber 
    ? (projectCode ? `${projectCode}-TC-${testCase.testCaseNumber}` : `TC-${testCase.testCaseNumber}`)
    : null;
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, testCase.id)}
      onDragEnd={onDragEnd}
      className="card"
      style={{
        cursor: isDragging ? 'grabbing' : 'grab',
        transition: 'all 0.2s',
        border: `1px solid ${theme.gridLine}`,
        backgroundColor: theme.surface,
        boxShadow: isDragging ? 'none' : '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        wordWrap: 'break-word',
        padding: '1rem',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100px',
        opacity: isDragging ? 0.4 : 1,
      }}
      onMouseEnter={(e) => {
        if (!isDragging) {
          setIsHovered(true);
          e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
          e.currentTarget.style.transform = 'translateY(-2px)';
        }
      }}
      onMouseLeave={(e) => {
        if (!isDragging) {
          setIsHovered(false);
          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
          e.currentTarget.style.transform = 'translateY(0)';
        }
      }}
    >
      {/* Header: Title, Edit, Delete */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, minWidth: 0 }}>
          <span className="drag-handle" title="Drag to move" style={{ cursor: 'grab', color: theme.textMuted }}>
            {Icons.dragHandle}
          </span>
          {displayId && (
            <span
              style={{
                fontSize: '0.7rem',
                fontWeight: 600,
                padding: '0.2rem 0.4rem',
                backgroundColor: theme.cyanGlow,
                color: theme.surface,
                borderRadius: '3px',
                flexShrink: 0,
                fontFamily: 'monospace'
              }}
              title="Test Case ID"
            >
              {displayId}
            </span>
          )}
          <div
            onClick={(e) => {
              e.stopPropagation();
              if (onView) onView(testCase);
            }}
            style={{
              fontSize: '1rem',
              fontWeight: '600',
              color: theme.textPrimary,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flex: 1,
              minWidth: 0,
              cursor: onView ? 'pointer' : 'default',
              transition: 'color 0.2s ease'
            }}
            onMouseEnter={(e) => { if (onView) e.currentTarget.style.color = theme.cyanGlow; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = theme.textPrimary; }}
            title={onView ? 'Click to view details' : undefined}
          >
            {testCase.title}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
          {/* Automation badges */}
          {testCase.automated && (
            <span 
              style={{ 
                fontSize: '0.65rem', 
                padding: '0.2rem 0.4rem',
                backgroundColor: theme.success,
                color: 'white',
                borderRadius: '3px',
                fontWeight: 600
              }}
              title="This test case is automated"
            >
              ✓ AUTO
            </span>
          )}
          {!testCase.automated && testCase.automationCandidate && (
            <span 
              style={{ 
                fontSize: '0.65rem', 
                padding: '0.2rem 0.4rem',
                backgroundColor: theme.warning,
                color: theme.surface,
                borderRadius: '3px',
                fontWeight: 600
              }}
              title="Ready to automate"
            >
              🎯
            </span>
          )}
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              opacity: isHovered ? 1 : 0,
              transition: 'opacity 0.2s ease',
              pointerEvents: isHovered ? 'auto' : 'none'
            }}
          >
            {onEdit && (
              <button
                className="btn btn-secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(testCase.id);
                }}
                style={{
                  padding: '0.5rem',
                  minWidth: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Edit test case"
              >
                {Icons.edit}
              </button>
            )}
            <button
              className="btn btn-secondary"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(testCase.id);
              }}
              style={{
                padding: '0.5rem',
                minWidth: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.danger
              }}
              title="Delete test case"
            >
              {Icons.delete}
            </button>
          </div>
        </div>
      </div>

      {/* Separator */}
      <div style={{ height: '1px', background: theme.gridLine, marginBottom: '0.75rem' }} />

      {/* Content area */}
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        {/* Description */}
        {testCase.description && (
          <div style={{ 
            lineHeight: '1.4', 
            fontSize: '0.875rem', 
            color: theme.textSecondary
          }}>
            {testCase.description}
          </div>
        )}

        {/* Change History Link */}
        {onShowChangeHistory && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onShowChangeHistory(testCase);
            }}
            className="btn btn-ghost"
            style={{ 
              padding: '0.25rem 0.5rem',
              fontSize: '0.7rem',
              color: theme.textMuted,
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
              marginTop: '0.5rem',
              alignSelf: 'flex-start'
            }}
            title="View change history"
          >
            <span>📝</span>
            {testCase.updatedBy && getUserName && getUserName(testCase.updatedBy) ? (
              <span>Updated by {getUserName(testCase.updatedBy)}</span>
            ) : testCase.createdBy && getUserName && getUserName(testCase.createdBy) ? (
              <span>Created by {getUserName(testCase.createdBy)}</span>
            ) : (
              <span>View history</span>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default TestCaseCard;
